import React, {useState, useEffect} from 'react'
import {Modal} from 'react-bootstrap'
import {Button, Select, TextInput, Textarea} from '@mantine/core'
import {toast} from 'react-toastify'
import {
  getTools,
  createTool,
  updateTool,
  deleteTool,
  activateTool,
  getToolCategories,
  Tool,
  ToolInput,
} from '../../../api/TimesheetAPI'
import {PageHeader} from '../../components/PageHeader/PageHeader'
import {DataTable} from '../../components/DataTable/DataTable'
import {ActionButtons} from '../../components/ActionButtons/ActionButtons'
import {StatusBadge} from '../../components/StatusBadge/StatusBadge'
import {FormWrapper} from '../../components/FormWrapper/FormWrapper'
import {useApiCall} from '../../hooks/useApiCall'

const Tools: React.FC = () => {
  const [tools, setTools] = useState<Tool[]>([])
  const [filteredTools, setFilteredTools] = useState<Tool[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingTool, setEditingTool] = useState<Tool | null>(null)
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL')
  const [statusFilter, setStatusFilter] = useState<string>('ALL')
  const [searchTerm, setSearchTerm] = useState<string>('')

  const {loading, error, execute} = useApiCall<any>()

  const [formData, setFormData] = useState<ToolInput>({
    name: '',
    category: '',
    description: '',
    isActive: true,
  })

  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  // Load tools and categories
  const loadTools = async () => {
    await execute(async () => {
      const response = await getTools()
      setTools(response.data.data)
      return response.data.data
    })
  }

  const loadCategories = async () => {
    try {
      const response = await getToolCategories()
      setCategories(response.data.data)
    } catch (err) {
      console.error('Failed to load categories:', err)
    }
  }

  useEffect(() => {
    loadTools()
    loadCategories()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Apply filters
  useEffect(() => {
    let filtered = tools

    if (categoryFilter !== 'ALL') {
      filtered = filtered.filter((tool) => tool.category === categoryFilter)
    }

    if (statusFilter === 'ACTIVE') {
      filtered = filtered.filter((tool) => tool.isActive)
    } else if (statusFilter === 'INACTIVE') {
      filtered = filtered.filter((tool) => !tool.isActive)
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (tool) =>
          tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tool.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tool.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    setFilteredTools(filtered)
  }, [tools, categoryFilter, statusFilter, searchTerm])

  const openModal = (tool?: Tool) => {
    if (tool) {
      setEditingTool(tool)
      setFormData({
        name: tool.name,
        category: tool.category || '',
        description: tool.description || '',
        isActive: tool.isActive,
      })
    } else {
      setEditingTool(null)
      setFormData({
        name: '',
        category: '',
        description: '',
        isActive: true,
      })
    }
    setFormErrors({})
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingTool(null)
    setFormData({
      name: '',
      category: '',
      description: '',
      isActive: true,
    })
    setFormErrors({})
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!formData.name.trim()) {
      errors.name = 'Tool name is required'
    } else if (formData.name.length < 2) {
      errors.name = 'Tool name must be at least 2 characters'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    await execute(async () => {
      if (editingTool) {
        const response = await updateTool(editingTool.id, formData)
        toast.success('Tool updated successfully')
        return response.data
      } else {
        const response = await createTool(formData)
        toast.success('Tool created successfully')
        return response.data
      }
    })

    closeModal()
    loadTools()
    loadCategories()
  }

  const handleDelete = async (tool: Tool) => {
    if (!window.confirm(`Are you sure you want to delete "${tool.name}"?`)) {
      return
    }

    await execute(async () => {
      const response = await deleteTool(tool.id)
      toast.success(response.data.message || 'Tool deleted successfully')
      return response.data
    })

    loadTools()
  }

  const handleActivate = async (tool: Tool) => {
    await execute(async () => {
      const response = await activateTool(tool.id)
      toast.success('Tool activated successfully')
      return response.data
    })

    loadTools()
  }

  const columns = [
    {
      header: 'Tool Name',
      cell: (row: Tool) => <strong>{row.name}</strong>,
    },
    {
      header: 'Category',
      cell: (row: Tool) => (
        <span className='badge badge-light-info'>{row.category || 'Uncategorized'}</span>
      ),
    },
    {
      header: 'Description',
      cell: (row: Tool) => <span className='text-muted'>{row.description || '-'}</span>,
    },
    {
      header: 'Status',
      cell: (row: Tool) => <StatusBadge status={row.isActive ? 'Active' : 'Inactive'} />,
    },
    {
      header: 'Actions',
      cell: (row: Tool) => (
        <div className='d-flex gap-2'>
          <ActionButtons
            actions={[
              {
                type: 'edit',
                onClick: () => openModal(row),
                label: 'Edit',
              },
              ...(row.isActive ? [{
                type: 'delete' as const,
                onClick: () => handleDelete(row),
                label: 'Delete',
                confirm: {
                  title: 'Delete Tool',
                  text: `Are you sure you want to delete "${row.name}"?`,
                  icon: 'warning' as const,
                },
              }] : []),
            ]}
          />
          {!row.isActive && (
            <Button size='xs' color='green' onClick={() => handleActivate(row)}>
              Activate
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <>
      <div className='card card-flush shadow-sm'>
        <div className='card-header'>
          <h3 className='card-title'>Tools Management</h3>
          <div className='card-toolbar'>
            <Button onClick={() => openModal()}>
              Add Tool
            </Button>
          </div>
        </div>
        <div className='card-body'>
        {/* Filters */}
        {/* <div className='row mb-4'>
          <div className='col-md-3'>
            <div className='position-relative'>
              <TextInput
                placeholder='Search tools...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className='col-md-3'>
            <Select
              label='Category'
              value={categoryFilter}
              onChange={(value) => setCategoryFilter(value || 'ALL')}
              data={[
                {value: 'ALL', label: 'All Categories'},
                ...categories.map((cat) => ({value: cat, label: cat})),
              ]}
            />
          </div>
          <div className='col-md-3'>
            <Select
              label='Status'
              value={statusFilter}
              onChange={(value) => setStatusFilter(value || 'ALL')}
              data={[
                {value: 'ALL', label: 'All Status'},
                {value: 'ACTIVE', label: 'Active Only'},
                {value: 'INACTIVE', label: 'Inactive Only'},
              ]}
            />
          </div>
          <div className='col-md-3 d-flex align-items-end'>
            <div className='text-muted'>
              Showing {filteredTools.length} of {tools.length} tools
            </div>
          </div>
        </div> */}
        {/* Filters */}
        <div className="row mb-4 align-items-end">
          {/* Search */}
          <div className="col-md-4">
            <TextInput
              label="Search"
              placeholder="Search tools..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Category */}
          <div className="col-md-4">
            <Select
              label="Category"
              value={categoryFilter}
              onChange={(value) => setCategoryFilter(value || 'ALL')}
              data={[
                { value: 'ALL', label: 'All Categories' },
                ...categories.map((cat) => ({ value: cat, label: cat })),
              ]}
              withinPortal={false}
            />
          </div>

          {/* Status */}
          <div className="col-md-4">
            <Select
              label="Status"
              value={statusFilter}
              onChange={(value) => setStatusFilter(value || 'ALL')}
              data={[
                { value: 'ALL', label: 'All Status' },
                { value: 'ACTIVE', label: 'Active Only' },
                { value: 'INACTIVE', label: 'Inactive Only' },
              ]}
              withinPortal={false}
            />
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className='alert alert-danger' role='alert'>
            {error}
          </div>
        )}

        {/* Table */}
        <DataTable
          data={filteredTools}
          columns={columns}
          loading={loading}
          emptyMessage='No tools found. Click "Add Tool" to create one.'
        />
        </div>
      </div>

      {/* Add/Edit Modal */}
      {/* <Modal show={isModalOpen} onHide={closeModal} size='lg'>
        <FormWrapper
          title={editingTool ? 'Edit Tool' : 'Add Tool'}
          show={isModalOpen}
          onSubmit={handleSubmit}
          onCancel={closeModal}
          loading={loading}
        >
          <TextInput
            label='Tool Name'
            placeholder='e.g., Adobe Suite, Figma, Catia'
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            error={formErrors.name}
            required
          />

          <Select
            label='Category'
            placeholder='Select or type a category'
            value={formData.category}
            onChange={(value) => setFormData({...formData, category: value || ''})}
            data={categories.map((cat) => ({value: cat, label: cat}))}
            searchable
            creatable
            getCreateLabel={(query) => `+ Create "${query}"`}
            onCreate={(query) => {
              setCategories([...categories, query])
              setFormData({...formData, category: query})
              return query
            }}
            mt='md'
          />

          <Textarea
            label='Description'
            placeholder='Brief description of this tool'
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            rows={3}
            mt='md'
          />

          {editingTool && (
            <Select
              label='Status'
              value={formData.isActive ? 'true' : 'false'}
              onChange={(value) => setFormData({...formData, isActive: value === 'true'})}
              data={[
                {value: 'true', label: 'Active'},
                {value: 'false', label: 'Inactive'},
              ]}
              mt='md'
            />
          )}
        </FormWrapper>
      </Modal> */}
      {/* Add/Edit Modal */}
      <FormWrapper
        title={editingTool ? 'Edit Tool' : 'Add Tool'}
        show={isModalOpen}
        onSubmit={handleSubmit}
        onCancel={closeModal}
        loading={loading}
      >
        <TextInput
          label='Tool Name'
          placeholder='e.g., Adobe Suite, Figma, Catia'
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          error={formErrors.name}
          required
        />

        <Select
          label='Category'
          placeholder='Select or type a category'
          value={formData.category}
          onChange={(value) => setFormData({...formData, category: value || ''})}
          data={categories.map((cat) => ({value: cat, label: cat}))}
          searchable
          creatable
          getCreateLabel={(query) => `+ Create "${query}"`}
          onCreate={(query) => {
            setCategories([...categories, query])
            setFormData({...formData, category: query})
            return query
          }}
          mt='md'
          withinPortal={false}
        />

        <Textarea
          label='Description'
          placeholder='Brief description of this tool'
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          rows={3}
          mt='md'
        />

        <Select
          label='Status'
          value={formData.isActive ? 'true' : 'false'}
          onChange={(value) =>
            setFormData({...formData, isActive: value === 'true'})
          }
          data={[
            {value: 'true', label: 'Active'},
            {value: 'false', label: 'Inactive'},
          ]}
          mt='md'
          withinPortal={false}
        />
      </FormWrapper>
    </>
  )
}

export default Tools

